import type { EvidenceRecord, FindingStatus, ScanStatus, Severity } from "../api/types.js";
import { titleCase } from "../lib/format.js";

type BadgeTone =
  | "critical"
  | "high"
  | "medium"
  | "low"
  | "positive"
  | "warning"
  | "neutral"
  | "muted"
  | "info";

export function Badge({
  label,
  tone = "neutral",
  title,
}: {
  label: string;
  tone?: BadgeTone;
  title?: string | undefined;
}) {
  return (
    <span className={`badge badge--${tone}`} title={title}>
      {label}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: Severity }) {
  return <Badge label={titleCase(severity)} tone={severity} />;
}

export function StatusBadge({ status }: { status: FindingStatus }) {
  const tone: BadgeTone =
    status === "confirmed"
      ? "positive"
      : status === "needs_human"
        ? "warning"
        : status === "refuted"
          ? "muted"
          : status === "suppressed"
            ? "muted"
            : "info";
  return (
    <Badge
      label={titleCase(status)}
      tone={tone}
      title={
        status === "needs_human"
          ? "Evidence exists, but independent corroboration or human judgment is still required before confirmation."
          : undefined
      }
    />
  );
}

export function ReplayBadge({ status }: { status: EvidenceRecord["replayStatus"] }) {
  const tone: BadgeTone =
    status === "matched"
      ? "positive"
      : status === "mismatch"
        ? "critical"
        : status === "unavailable"
          ? "warning"
          : "neutral";
  return <Badge label={titleCase(status)} tone={tone} />;
}

export function ScanStatusBadge({ status }: { status: ScanStatus }) {
  const tone: BadgeTone =
    status === "completed"
      ? "positive"
      : status === "completed_with_errors"
        ? "warning"
        : status === "failed"
          ? "critical"
          : status === "cancelled"
            ? "muted"
            : status === "queued"
              ? "neutral"
              : "info";
  return <Badge label={titleCase(status)} tone={tone} />;
}
